/** @jsx jsx */

import { jsx, css } from '@emotion/core';

import React, { Fragment } from 'react';
import { Box, Typography, Divider, FormHelperText } from '@material-ui/core';
import useContentMgmt from '../../../hooks/useContentMgmt';
import colors from '../../../themes/main-colors';
import { styled } from '@material-ui/core/styles';
import RadioButtonGroup from '../../pge-plus-common/RadioButtonGroup';
import microcopyGroupIds from '../../pge-plus-common/microcopyGroupIds';
import { Question } from '../../pge-plus-common/Typography';
import useFormState from '../../../hooks/useFormState';
import { RouteComponentProps } from '@reach/router';
import { ProceedOrCancelButton } from '../../pge-plus-common/StepperForm';
import routes from '../../../routes';
import { enrollWithVehicleOrChargerValidateFunction } from '../../pge-plus/ev-charger/rebate-only-flow/rebateOnlyForm.rules';
import { navigate } from 'gatsby';
import { useEVChargerFormState } from '../../../providers/EVChargerFormStateProvider';
import useWrapWithLoader from '../../../hooks/useWrapWithLoading';
import useUtil from '../../../hooks/pge-plus/useUtil';
import { SaveAndComeBackLink } from '../../pge-plus-common/SaveAndComeBackLink';
import {
  ApplicationEvSectionType,
  ApplicationSectionStatus,
  EvEnrollmentPreference,
  SectionEvChargerSelection,
} from '../../../__generated__/pgeplus-types';
import useUpdateEVSections from '../../../hooks/pge-plus/useUpdateEVSections';
import { useRebateEligibility } from '../../../hooks/pge-plus/useRebatesEligibility';
import { pickChargerAndVehicleSelectionFields, pickChargerValidationResultFields, selectChargerFormStateAdapter } from '../../pge-plus-common/utils/pgeplusFormstateAdapters';

const PREFIX = 'enrollChoice';
const classes = {
  submit: `${PREFIX}-submit`,
  errorMessage: `${PREFIX}-errorMessage`,
};

// TODO jss-to-styled codemod: The Fragment root was replaced by div. Change the tag if needed.
const Root = styled(Box)(({ theme: theme2 }) => ({
  [`& .${classes.submit}`]: {
    backgroundColor: colors.orange,
    borderColor: colors.orange,
    '&:hover': {
      backgroundColor: colors.orange,
      borderColor: colors.orange,
    },
    [theme2.breakpoints.down('md')]: {
      width: '100%',
    },
  },
  [`& .${classes.errorMessage}`]: {
    fontFamily: 'Untitled-Sans',
    fontSize: '1rem !important',
  },
}));

interface EnrollWithVehicleOrChargerProps extends RouteComponentProps {
  children?: React.ReactNode;
}

export const EnrollWithVehicleOrCharger: React.FC<EnrollWithVehicleOrChargerProps> = props => {
  const { content: contentMCG, richText } = useContentMgmt(microcopyGroupIds.PGE_PLUS_EV_CHARGER_FLOW_MCG);
  const { wrapWithLoader } = useWrapWithLoader();
  const { setErrorNotification } = useUtil();
  const {
    formState: { selectCharger, application, rebateEligibility },
    updateFormState,
    getSectionData,
  } = useEVChargerFormState();

  const sectionDataSelectCharger: SectionEvChargerSelection = getSectionData(
    ApplicationEvSectionType.EvChargerSelection,
  );

  const { updateSectionChargerSelection } = useUpdateEVSections();
  const { updateSectionEVRebatesEligibility } = useRebateEligibility();

  const reopenRebatesIfPreferenceChanged = async (
    enrollmentPreference: EvEnrollmentPreference,
  ) => {
    const hasRebateFlowAnswers = Boolean(
      rebateEligibility?.isHaveWifi ||
        rebateEligibility?.connectVehicleAManufacturerApp ||
        rebateEligibility?.isOwnTesla ||
        rebateEligibility?.vin,
    );
    if (
      selectCharger?.enrollmentPreference === enrollmentPreference ||
      !hasRebateFlowAnswers ||
      rebateEligibility?.chargerForExistingVehicle
    ) {
      return;
    }

    await updateSectionEVRebatesEligibility(
      rebateEligibility,
      false,
      ApplicationSectionStatus.Pending,
      false,
    );
  };

  const subText1 = { fontSize: 20, marginBottom: 16 };
  const boldText = { fontWeight: 700 };

  const form = useFormState<{ choice: EvEnrollmentPreference | '' }>(
    { choice: selectCharger?.enrollmentPreference || '' },
  );

  // NOTE: Save to the backend now, then sync form state to match.
  const submitSection = async (
    enrollmentPreference: EvEnrollmentPreference,
    saveAndComeBack: boolean = false,
  ) => {
    await reopenRebatesIfPreferenceChanged(enrollmentPreference);
    const response = await updateSectionChargerSelection(
      selectChargerFormStateAdapter().fromFormState(
        { ...pickChargerAndVehicleSelectionFields(selectCharger), enrollmentPreference },
        application?.id!,
        sectionDataSelectCharger?.id,
        saveAndComeBack,
      ),
    );

    if (response.data?.updateSectionEVChargerSelection?.validation?.success !== true) {
      throw new Error();
    }

    const sectionData = response.data.updateSectionEVChargerSelection.section?.data;
    updateFormState('selectCharger', {
      ...pickChargerAndVehicleSelectionFields(selectCharger),
      ...pickChargerValidationResultFields(sectionData),
    });
  };

  const onSubmit = wrapWithLoader(async (data: { choice: EvEnrollmentPreference | '' }) => {
    try {
      await submitSection(data.choice as EvEnrollmentPreference);

      // Navigate to contact info for both charger and vehicle flows
      void navigate(routes.PGE_PLUS_EV_CHARGERS_CONTACT_INFO);
    } catch (e) {
      setErrorNotification(true);
    }
  });

  const handleSaveAndExit = wrapWithLoader(async () => {
    try {
      // NOTE: Only save if users actually picked a preference..
      if (form.values.choice) {
        await submitSection(form.values.choice, true);
      }

      void navigate(routes.ACCOUNT);
    } catch {
      setErrorNotification(true);
    }
  });

  return (
    <Root>
      <form onSubmit={form.submit(onSubmit)} noValidate>
        <Box 
          marginBottom={2}
          css={css`& a {text-decoration: none;}`}
        >
          <Box>
            <Typography css={{ ...subText1, ...boldText }}>
              {contentMCG.get('PGE_PLUS_EV_CHARGER_FLOW_ENROLL_CHARGER_OR_VEHICLE_QUESTION_MAIN')}
            </Typography>
          </Box>
          <Box mt={3}>
            <Typography variant="body1">
              {contentMCG.get('PGE_PLUS_EV_CHARGER_FLOW_ENROLL_CHARGER_OR_VEHICLE_DESCRIPTION')}
            </Typography>
          </Box>
          <Box mt={3}>
            <Typography variant="body1" component="div">
              {richText(contentMCG.get('PGE_PLUS_EV_CHARGER_FLOW_ENROLL_CHARGER_OR_VEHICLE_DESCRIPTION_CHARGER'))}
            </Typography>
          </Box>
          <Box mt={3}>
            <Typography variant="body1" component="div">
              {richText(contentMCG.get('PGE_PLUS_EV_CHARGER_FLOW_ENROLL_CHARGER_OR_VEHICLE_DESCRIPTION_VEHICLE'))}
            </Typography>
          </Box>
          <Box mt={3}>
            <Question
              title={contentMCG.get('PGE_PLUS_EV_CHARGER_FLOW_ENROLL_CHARGER_OR_VEHICLE_QUESTION')}
            />
            <Box
              mt={3}
              css={{
                width: 'max-content',
                marginLeft: 'auto',
                marginRight: 'auto',
              }}
            >
              <RadioButtonGroup
                options={[
                  { label: contentMCG.get('PGE_PLUS_EV_CHARGER_FLOW_ENROLL_CHARGER_OR_VEHICLE_USE_MY_CHARGER') || '',
                    value: EvEnrollmentPreference.UseMyCharger },
                  { label: contentMCG.get('PGE_PLUS_EV_CHARGER_FLOW_ENROLL_CHARGER_OR_VEHICLE_USE_MY_VEHICLE') || '',
                    value: EvEnrollmentPreference.UseMyVehicle },
                ]}
                state={form.values.choice}
                setState={choice => {
                  void form.setValue('choice', choice as EvEnrollmentPreference);
                }}
              />
            </Box>
            <Divider css={{ color: colors.gray93, margin: '30px 0 0px 0' }} />
          </Box>
        
          <ProceedOrCancelButton
            back={routes.PGE_PLUS_EV_CHARGERS_ENROLL_WITH_VEHICLE_AND_CHARGER}
            disable={!form.values.choice}
          />
        </Box>
      </form>
      <Box mt={6}>
        <SaveAndComeBackLink
          content={contentMCG}
          onSaveAndExit={handleSaveAndExit}
        />
      </Box>
    </Root>
  );
};
